<template>
    <v-container class="py-0 px-0" fluid v-if="this.$store.state.signedIn">

        <v-layout justify-center wrap>
            <!-- start of org rules -->
            <v-flex xs12 class="py-0">
                <!-- Toolbar -->
                <v-toolbar dark flat :color="use_color" v-if="showOrgASNs">
                    <v-toolbar-title :style="$store.state.textFormatting.headers"> {{organisation}} Rules (NEW) </v-toolbar-title>
                    <v-progress-circular v-if="loading" indeterminate color="white"></v-progress-circular>
                    <v-spacer></v-spacer>
                    <v-flex shrink align-center justify-center>
                        <br />
                        <v-switch v-model="showAdvanced" @change="advancedChange()">
                            <template v-slot:label>
                                <div :style="$store.state.textFormatting.main"><b>Advanced settings</b></div>
                            </template>
                        </v-switch>
                    </v-flex>
                    <downloadcsv
                        class   = "btn btn-default"
                        :data   = "organisation_download_rules"
                        name = "orgRules.csv">
                        <v-tooltip top eager color="primary" id="exit-app">
                            <template v-slot:activator="{ on }">
                                <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Export Org Rules </v-btn>
                            </template>
                            <span :style="$store.state.textFormatting.main">CSV of organisation Rules</span>
                        </v-tooltip>        
                    </downloadcsv>
                    
                    <info>
                        <template slot>
                            Add an Alert Rule to your organisation, or remove/ edit existing rule.
                        </template>
                         <template slot="body">
                            The alerting mechanism works in the following way: <br />
                            
                            <p> <h2> STANDARD ALERTING (no advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define a Rule based on an ASN or Prefix, give it a name (required), and will be alerted upon <b>ownership assertions</b>, <b>Path Changes</b> and/or <b>First Seen Prefixes</b> where the Prefix(es) and ASN(s) are matched in observed BGP messages. </li>
                                <li> Users can specify <b>which event types to be alerted upon</b>, of which there are 3 in total: 
                                    <ul>
                                        <li><b>Ownership Assertions</b></li>
                                        <li><b>Path Changes</b></li>
                                        <li><b>First Seens</b></li>
                                    </ul>
                                    There are descriptions of each of these in <b>hover over panels throughout the page - hover your cursor where you see any of these terms</b>
                                </li>
                            </ul>

                            <ul>
                                <li> The user can also decide whether to receive emails from these alerts, again split out by the event type </li>
                                <li> Hijack and Impact score thresholds can also be set, which can further refine down the amount of alerts you will receive. </li>
                                <li>
                                    The scores of the <b> event </b> are compared with the <b> thresholds </b>. If hijack <b> and/or (set by the user for each ASN - default AND) </b>
                                    impact scores are <b> greater than or equal to </b> the given thresholds, a notification/ alert is created in the mySQL table. There are some predefined thresholds:
                                    <ul>
                                        <li> <b> Regular: H = 30, I = 10, V = 5 </b> for when you are interested in some potentially dangerous anomalies, but not the less dangerous ones </li>
                                        <li> <b> High Importance: H = 20, I = 8, V = 2 </b> for when you want to know about most anomalies, but not those we deem very unlikely to be hijack events </li>
                                        <li> <b> Low Importance: H = 50, I = 25, V = 10 </b> for when you only need to see the very high risk anomalies </li>
                                        <li> <b> Show All: H = 0, I = 0, V = 0 (default)</b> will trigger alerts for all incoming events </li>
                                    </ul>
                                </li>
                                
                                <li> When an alert occurs with emails turned on, the following occurs:
                                    <ul>
                                        <li> An initial email is sent out, alerting the user of the alert beginning </li>
                                        <li> When additional events occur with the same basic data (in the same 5 min window), and the total 
                                            passes the <b>Repeat Email Alert Threshold - set by the user</b>, an additional email is sent out </li>
                                        <li> This continues until the 5 minute window elapses, at which point a new notification / alert will 
                                            begin </li>
                                    </ul>
                                </li>
                                <li> <b>Lastly</b>, the user may add multiple ASNs to an <b>AS Group</b>. ASNs in a common group behave in one of the following, user defined, ways:
                                    <ul>
                                        <li><b>EXCLUDE:</b> Ownership assertions between ASNs in the group will <b>not create alerts</b> e.g. if I have AS 1 and AS 2 in a group and AS 2 asserts ownership over one of AS 1's prefixes, I will not be alerted </li>
                                        <li><b>HIJACK:</b> Ownership assertions between ASNs in the group will have their hijack scores overriden and set to the user defined score </li>
                                    </ul>
                                </li>
                            </ul>

                            <p> <h2> ADVANCED ALERTING (advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define score overrides for a given rule to raise the immportance of an alert. These scores are visible only to the Organisation or User.</li>
                            </ul>
                            <ul>
                                <li> A user can also define more detailed Prefix, Origin and Path based rules as follows:: 
                                    <ul>
                                        <li><b>Additional "Except" claused for Prefix, Origin and Path</b> - these allow the rules to be fine tuned to exclude alerts based on specific criteria. e.g. alert on ownership assertions for prefix 1.1.1.0/24 except where ASN is 1234</li>
                                        <li><b>Optional Origin Alerting Conditions</b> - where an origin is normally triggered as a "new" or "old" origin, rules can be tuned to be specific about the direction of change. e.g. alert on ownership assertions for prefix 1.1.1.90/24 only when ASN 1234 is the new origin</li>
                                        <li><b>Path Position</b> - this allows users to trigger alerts where an ASN is found "anywhere" in the path or at the "origin + 1" postion</li>
                                    </ul>
                                </li>
                            </ul>
                            <br />
                            <b> NOTE: Updates to thresholds do not work retrospectively, so will not alter previously created alerts and alert counts. There is also a delay in the analytics engine picking up the changes, so you may continue to receive alerts after making changes. </b>
                        </template>
                    </info>
                </v-toolbar>  
                <!-- end of toolbar -->

                <!-- start of ORG rules -->
                <v-card v-if="showOrgASNs">  
                    <!-- data table -->
                    <v-data-table :headers="alertHeaders" :items="orgRules" 
                            :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" > 
                            <template slot="headerCell" slot-scope="props">
                                <v-tooltip top eager>
                                <span slot="activator" :style="$store.state.textFormatting.main">
                                    {{ props.header.text }}
                                </span>
                                <span :style="$store.state.textFormatting.main">
                                    {{ props.header.ad_text }}
                                </span>
                                </v-tooltip>
                            </template>   
                            <template slot="items" slot-scope="props">
                                <tr>
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main"> {{ props.item.alertgroup }} </td>
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main"> {{ props.item.alertname }} </td>
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main"> <div style="text-align:right">
                                        Assertions : <v-icon v-if="props.item.alerts.assertion"> check </v-icon><v-icon v-if="!props.item.alerts.assertion"> clear </v-icon>(<v-icon v-if="props.item.emails.assertion"> check </v-icon><v-icon v-if="!props.item.emails.assertion"> clear </v-icon>)<br >
                                        Path Changes :  <v-icon v-if="props.item.alerts.pathchange"> check </v-icon><v-icon v-if="!props.item.alerts.pathchange"> clear </v-icon>(<v-icon v-if="props.item.emails.pathchange"> check </v-icon><v-icon v-if="!props.item.emails.pathchange"> clear </v-icon>)<br >
                                        First Seen :  <v-icon v-if="props.item.alerts.firstseen"> check </v-icon><v-icon v-if="!props.item.alerts.firstseen"> clear </v-icon>(<v-icon v-if="props.item.emails.firstseen"> check </v-icon><v-icon v-if="!props.item.emails.firstseen"> clear </v-icon>)<br ></div>
                                         </td>
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main">
                                        <span v-for="origin in props.item.filter.origin.is">{{ origin }}<br /></span></td>
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main">
                                        <span v-for="prefix in props.item.filter.prefix.is">{{ prefix }}<br /></span></td>
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main">
                                        <span v-for="path in props.item.filter.path.contains">{{ path }}<br /></span></td>

                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.hijack }}</td>
                                    
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.andor }}</td>
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.impact }}</td>
                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.emails.repeat }}</td>

                                    <td class="text-xs-center special" :style="$store.state.textFormatting.main"> {{ props.item.notification_count }} </td>
                                </tr>
                                
                            </template>
                        <template slot="no-data">
                            <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                            No Organisation Rules, click 'ADD RULE +' to add one
                            </v-alert>
                        </template>
                    </v-data-table>


                </v-card>
            </v-flex>
            <v-flex xs12 class="py-0">
                <v-toolbar dark flat :color="use_color" v-if="showUserASNs">
                    <v-toolbar-title  :style="$store.state.textFormatting.headers"> {{username}}'s Rules </v-toolbar-title>
                    <v-progress-circular v-if="loading2" indeterminate color="white"></v-progress-circular>
                    <v-spacer></v-spacer>
                    <v-flex shrink align-center justify-center>
                        <br />
                        <v-switch v-model="showAdvanced" @change="advancedChange()">
                            <template v-slot:label>
                                <div :style="$store.state.textFormatting.main"><b>Advanced settings</b></div>
                            </template>
                        </v-switch>
                    </v-flex>
                    <downloadcsv
                        class   = "btn btn-default"
                        :data   = "user_download_rules"
                        name = "userRules.csv">
                        <v-tooltip top eager color="primary" id="exit-app">
                            <template v-slot:activator="{ on }">
                                <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Export User Rules </v-btn>
                            </template>
                            <span :style="$store.state.textFormatting.main">CSV of user Rules</span>
                        </v-tooltip>        
                    </downloadcsv>
                    
                    <info>
                        <template slot>
                            Add an Alert Rule to your specific user, or remove/ edit existing rule.
                        </template>
                         <template slot="body">
                            The alerting mechanism works in the following way: <br />
                            
                            <p> <h2> STANDARD ALERTING (no advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define a Rule based on an ASN or Prefix, give it a name (required), and will be alerted upon <b>ownership assertions</b>, <b>Path Changes</b> and/or <b>First Seen Prefixes</b> where the Prefix(es) and ASN(s) are matched in observed BGP messages. </li>
                                <li> Users can specify <b>which event types to be alerted upon</b>, of which there are 3 in total: 
                                    <ul>
                                        <li><b>Ownership Assertions</b></li>
                                        <li><b>Path Changes</b></li>
                                        <li><b>First Seens</b></li>
                                    </ul>
                                    There are descriptions of each of these in <b>hover over panels throughout the page - hover your cursor where you see any of these terms</b>
                                </li>
                            </ul>

                            <ul>
                                <li> The user can also decide whether to receive emails from these alerts, again split out by the event type </li>
                                <li> Hijack and Impact score thresholds can also be set, which can further refine down the amount of alerts you will receive. </li>
                                <li>
                                    The scores of the <b> event </b> are compared with the <b> thresholds </b>. If hijack <b> and/or (set by the user for each ASN - default AND) </b>
                                    impact scores are <b> greater than or equal to </b> the given thresholds, a notification/ alert is created in the mySQL table. There are some predefined thresholds:
                                    <ul>
                                        <li> <b> Regular: H = 30, I = 10, V = 5 </b> for when you are interested in some potentially dangerous anomalies, but not the less dangerous ones </li>
                                        <li> <b> High Importance: H = 20, I = 8, V = 2 </b> for when you want to know about most anomalies, but not those we deem very unlikely to be hijack events </li>
                                        <li> <b> Low Importance: H = 50, I = 25, V = 10 </b> for when you only need to see the very high risk anomalies </li>
                                        <li> <b> Show All: H = 0, I = 0, V = 0 (default)</b> will trigger alerts for all incoming events </li>
                                    </ul>
                                </li>
                                
                                <li> When an alert occurs with emails turned on, the following occurs:
                                    <ul>
                                        <li> An initial email is sent out, alerting the user of the alert beginning </li>
                                        <li> When additional events occur with the same basic data (in the same 5 min window), and the total 
                                            passes the <b>Repeat Email Alert Threshold - set by the user</b>, an additional email is sent out </li>
                                        <li> This continues until the 5 minute window elapses, at which point a new notification / alert will 
                                            begin </li>
                                    </ul>
                                </li>
                                <li> <b>Lastly</b>, the user may add multiple ASNs to an <b>AS Group</b>. ASNs in a common group behave in one of the following, user defined, ways:
                                    <ul>
                                        <li><b>EXCLUDE:</b> Ownership assertions between ASNs in the group will <b>not create alerts</b> e.g. if I have AS 1 and AS 2 in a group and AS 2 asserts ownership over one of AS 1's prefixes, I will not be alerted </li>
                                        <li><b>HIJACK:</b> Ownership assertions between ASNs in the group will have their hijack scores overriden and set to the user defined score </li>
                                    </ul>
                                </li>
                            </ul>

                            <p> <h2> ADVANCED ALERTING (advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define score overrides for a given rule to raise the immportance of an alert. These scores are visible only to the Organisation or User.</li>
                            </ul>
                            <ul>
                                <li> A user can also define more detailed Prefix, Origin and Path based rules as follows:: 
                                    <ul>
                                        <li><b>Additional "Except" claused for Prefix, Origin and Path</b> - these allow the rules to be fine tuned to exclude alerts based on specific criteria. e.g. alert on ownership assertions for prefix 1.1.1.0/24 except where ASN is 1234</li>
                                        <li><b>Optional Origin Alerting Conditions</b> - where an origin is normally triggered as a "new" or "old" origin, rules can be tuned to be specific about the direction of change. e.g. alert on ownership assertions for prefix 1.1.1.90/24 only when ASN 1234 is the new origin</li>
                                        <li><b>Path Position</b> - this allows users to trigger alerts where an ASN is found "anywhere" in the path or at the "origin + 1" postion</li>
                                    </ul>
                                </li>
                            </ul>
                            <br />
                            <b> NOTE: Updates to thresholds do not work retrospectively, so will not alter previously created alerts and alert counts. There is also a delay in the analytics engine picking up the changes, so you may continue to receive alerts after making changes. </b>
                        </template>
                    </info>
                </v-toolbar>  
                <v-card v-if="showUserASNs">  

                    <v-data-table :headers="alertHeaders" :items="userRules" :key="id"
                            :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" > 
                            <template slot="headerCell" slot-scope="props">
                                <v-tooltip top eager>
                                <span slot="activator" :style="$store.state.textFormatting.main">
                                    {{ props.header.text }}
                                </span>
                                <span :style="$store.state.textFormatting.main">
                                    {{ props.header.ad_text }}
                                </span>
                                </v-tooltip>
                            </template>   
                            <template slot="items" slot-scope="props">
                                <tr>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alertgroup }} </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alertname }} </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> <div style="text-align:right">
                                        Assertions : <v-icon v-if="props.item.alerts.assertion"> check </v-icon><v-icon v-if="!props.item.alerts.assertion"> clear </v-icon>(<v-icon v-if="props.item.emails.assertion"> check </v-icon><v-icon v-if="!props.item.emails.assertion"> clear </v-icon>)<br >
                                        Path Changes :  <v-icon v-if="props.item.alerts.pathchange"> check </v-icon><v-icon v-if="!props.item.alerts.pathchange"> clear </v-icon>(<v-icon v-if="props.item.emails.pathchange"> check </v-icon><v-icon v-if="!props.item.emails.pathchange"> clear </v-icon>)<br >
                                        First Seen :  <v-icon v-if="props.item.alerts.firstseen"> check </v-icon><v-icon v-if="!props.item.alerts.firstseen"> clear </v-icon>(<v-icon v-if="props.item.emails.firstseen"> check </v-icon><v-icon v-if="!props.item.emails.firstseen"> clear </v-icon>)<br ></div>
                                          </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="origin in props.item.filter.origin.is">{{ origin }}<br /></span></td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="prefix in props.item.filter.prefix.is">{{ prefix }}<br /></span></td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="path in props.item.filter.path.contains">{{ path }}<br /></span></td>

                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.hijack }}</td>
                                    
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.andor }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.impact }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.emails.repeat }}</td>

                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.notification_count }} </td>
                                </tr>
                                
                            </template>
                        <template slot="no-data">
                            <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                            No User Rules, click 'ADD RULE +' to add one
                            </v-alert>
                        </template>
                    </v-data-table>

                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import { API, graphqlOperation } from 'aws-amplify';
//import store from '../../store';
import info from './infopopout.vue';
import downloadcsv from 'vue-json-csv';
import { findParentComponentByName } from "../../components/utils/findParent.js";


export default {
    name: 'userorgrules',
    components: {
        info,
        downloadcsv
    },
    props: 'type',
    describe: 'Shows ASN and prefix alert rules, can be set to be user or org specific',

    data() {
            return {
                alertHeaders : [
                    {
                    text: "Group",
                    ad_text: "Rule Group",
                    value: "alertGroup",
                    align: "centre",
                    }],
                org_user : '',
                use_color : 'secondary',
                showAdvanced : false,
                organisation : "",
                username : "",
                orgASGroups : [],
                userASGroups : [],
                loading : false,
                orgRules : [],
                userRules : [],
                ruleGroups: ['Default'],
                organisation_download_rules : [],
                user_download_rules: {},
                showOrgASNs: true,
                showUserASNs: true,
                loading2: false,
                rowsPerPageItems: [10, 20, 50, 100],
                pagination: {
                    rowsPerPage: 10,
                },

                
            }
        },
        beforeMount() {
            let app = findParentComponentByName(this, "app");
            app.checkSignedIn();

            this.org_user = this.$attrs.type;
            if ( this.org_user == 'user') {
                this.showOrgASNs=false;
                this.showUserASNs=true;
                this.use_color = 'accent'
            } else {
                this.showOrgASNs=true;
                this.showUserASNs=false;
                this.use_color = 'secondary'
            }

            try {
                this.organisation = this.$store.state.user.organisation;
                this.username = this.$store.state.user.username;
            } catch (err) {
                console.log(err);
                location.reload();
            }
            this.getRules("org");
            this.getRules("user");

            if (this.$store.state.orgASGroups) {
             //   console.log("pulling org AS groups from store");
                this.orgASGroups = this.$store.state.orgASGroups;
            } else {
             //   console.log("gathering org AS groups");
                this.getOrgGroups();
            }

            if (this.$store.state.userASGroups) {
             //   console.log("pulling user AS groups from store");
                this.userASGroups = this.$store.state.userASGroups;
            } else {
              //  console.log("gathering user AS groups");
                this.getUserGroups();
            }

            this.showAdvanced = this.$store.state.showSettings.advancedSettings;
            this.advancedChange();

        },
        methods: {
            advancedChange: async function () {
                if (this.showAdvanced) {
                    (this.alertHeaders = [
                    {
                        text: "Group",
                        ad_text: "Rule Group",
                        value: "alertGroup",
                        align: "centre",
                    },
                    {
                        text: "Name",
                        ad_text: "Rule Name",
                        value: "alertName",
                        align: "centre",
                    },
                    {
                        text: "Alert Type Settings",
                        ad_text: "Alerts",
                        value: "alerts",
                        align: "centre",
                    },
                    {
                        text: "Origin",
                        ad_text: "Origins",
                        value: "origin",
                        align: "centre",
                    },
                    {
                        text: "Prefix",
                        ad_text: "Prefixes",
                        value: "prefix",
                        align: "centre",
                    },
                    {
                        text: "Path",
                        ad_text: "Path ASNs",
                        value: "path",
                        align: "centre",
                    },
                    {
                        text: "H",
                        ad_text: "Hijack Score Threshold",
                        value: "hijack",
                        align: "centre",
                    },
                    {
                        text: "And/ Or?",
                        ad_text: "To do with scoring mechanism - see info. in top right",
                        value: "andor",
                        align: "centre",
                    },
                    {
                        text: "I",
                        ad_text: "Impact Score Threshold",
                        value: "impact",
                        align: "centre",
                    },
                    //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},

                    {
                        text: "Email Threshold (V)",
                        ad_text: "Validation Score Threshold",
                        value: "repeat",
                        align: "centre",
                    },
                    {
                        text: "Notifications",
                        ad_text: "Count of notifications over last week for given ASN",
                        value: "notification_count",
                        align: "centre",
                    },
                    ]),
                    (this.asnHeaders = [
                        {
                        text: "ASN",
                        ad_text: "Autonomous System Number",
                        value: "asn",
                        align: "centre",
                        },
                        {
                        text: "ASN Name",
                        ad_text: "User defined ASN name (for housekeeping)",
                        value: "asn_name",
                        align: "centre",
                        },
                        {
                        text: "AS Group",
                        ad_text:
                            "Define AS groups to omit / tweak alerts that occur between certain ASNs",
                        value: "as_group",
                        align: "centre",
                        },
                        {
                        text: "Own. Assertions?",
                        ad_text:
                            "Whether you will be alerted upon ownership assertions for this ASN (and receive emails on such alerts)",
                        value: "opf_split[0]",
                        align: "centre",
                        },
                        {
                        text: "Path Changes?",
                        ad_text:
                            "Whether you will be alerted on path change events for this ASN (and receive emails on such alerts)",
                        value: "opf_split[1]",
                        align: "centre",
                        },
                        {
                        text: "First Seens?",
                        ad_text:
                            "Whether you will be alerted upon first seen prefix events or not (and receive emails on such alerts)",
                        value: "opf_split[2]",
                        align: "centre",
                        },
                        {
                        text: "AS In Path Alerts?",
                        ad_text:
                            "Whether you will be alerted upon alerts with this AS in the path - excluding this AS as the peer (and receive emails on such alerts)",
                        value: "opf_split[3]",
                        align: "centre",
                        },
                        {
                        text: "H",
                        ad_text: "Hijack Score Threshold",
                        value: "H",
                        align: "centre",
                        },
                        {
                        text: "And/ Or?",
                        ad_text: "To do with scoring mechanism - see info. in top right",
                        value: "and_or",
                        align: "centre",
                        },
                        {
                        text: "I",
                        ad_text: "Impact Score Threshold",
                        value: "I",
                        align: "centre",
                        },
                        //{ text: 'Alert Type', ad_text:'Either path or origin - see info. in top right', value: 'alerting_type', align:'centre'},
                        //{ text: 'Ex. Peer?', ad_text:'Yes / No - see info. in top right', value: 'exclude_peer', align:'centre'},
                        //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},

                        {
                        text: "Email Threshold (V)",
                        ad_text:
                            "The amount of events required for an additional email alert to be sent out",
                        value: "V",
                        align: "centre",
                        },
                        {
                        text: "Notifications",
                        ad_text: "Count of notifications over last week for given ASN",
                        value: "notification_count",
                        align: "centre",
                        },
                    ]);
                } else {
                    (this.alertHeaders = [
                    {
                        text: "Group",
                        ad_text: "Rule Group",
                        value: "alertName",
                        align: "centre",
                    },
                    {
                        text: "Name",
                        ad_text: "Rule Name",
                        value: "alertName",
                        align: "centre",
                    },
                    {
                        text: "Alert Type Settings",
                        ad_text: "Alerts",
                        value: "alerts",
                        align: "centre"
                    },
                    {
                        text: "Origin",
                        ad_text: "Origin conditions",
                        value: "origin",
                        align: "centre",
                    },
                    {
                        text: "Prefix",
                        ad_text: "Prefix conditions",
                        value: "prefix",
                        align: "centre",
                    },
                    {
                        text: "Path",
                        ad_text: "Path conditions",
                        value: "path",
                        align: "centre",
                    },
                    //{ text: 'H', ad_text:'Hijack Score Threshold', value: 'H', align:'centre'},
                    //{ text: 'I', ad_text:'Impact Score Threshold', value: 'I', align:'centre'},
                    //{ text: 'V', ad_text:'Validation Score Threshold', value: 'V', align:'centre'},
                    //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},
                    //{ text: 'And/ Or?', ad_text:'To do with scoring mechanism - see info. in top right', value: 'and_or', align:'centre'},
                    {
                        text: "Notifications",
                        ad_text: "Count of notifications over last week for given ASN",
                        value: "notification_count",
                        align: "centre",
                    },
                    ]),
                    (this.asnHeaders = [
                        {
                        text: "ASN",
                        ad_text: "Autonomous System Number",
                        value: "asn",
                        align: "centre",
                        },
                        {
                        text: "ASN Name",
                        ad_text: "User defined ASN name (for housekeeping)",
                        value: "asn_name",
                        align: "centre",
                        },
                        //{ text: 'AS Group', ad_text:'Define AS groups to omit / tweak alerts that occur between certain ASNs', value: 'as_group', align:'centre'},
                        //{ text: 'Own. Assertions?', ad_text:'Whether you will be alerted upon ownership assertions for this ASN (and receive emails on such alerts)', value: 'opf_split[0]', align:'centre'},
                        //{ text: 'Path Changes?', ad_text:'Whether you will be alerted on path change events for this ASN (and receive emails on such alerts)', value: 'opf_split[1]', align:'centre'},
                        //{ text: 'First Seens?', ad_text:'Whether you will be alerted upon first seen prefix events or not (and receive emails on such alerts)', value: 'opf_split[2]', align:'centre'},
                        //{ text: 'AS In Path Alerts?', ad_text:'Whether you will be alerted upon alerts with this AS in the path - excluding this AS as the peer (and receive emails on such alerts)', value: 'opf_split[3]', align:'centre'},
                        //{ text: 'H', ad_text:'Hijack Score Threshold', value: 'H', align:'centre'},
                        //{ text: 'I', ad_text:'Impact Score Threshold', value: 'I', align:'centre'},
                        //{ text: 'Alert Type', ad_text:'Either path or origin - see info. in top right', value: 'alerting_type', align:'centre'},
                        //{ text: 'Ex. Peer?', ad_text:'Yes / No - see info. in top right', value: 'exclude_peer', align:'centre'},
                        //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},
                        //{ text: 'And/ Or?', ad_text:'To do with scoring mechanism - see info. in top right', value: 'and_or', align:'centre'},
                        //{ text: 'V', ad_text:'Validation Score Threshold', value: 'V', align:'centre'},
                        {
                        text: "Notifications",
                        ad_text: "Count of notifications over last week for given ASN",
                        value: "notification_count",
                        align: "centre",
                        },
                    ]);
                }
            },

            getOrgGroups: async function () {
                //this.loading = true;

                var query =
                    `query getASNGroups {
                            getASNGroups (
                                p_organisation: "` +
                    this.organisation +
                    `",
                                p_user: "whole_organisation"
                            ) {
                                group_name,
                                exclude,
                                hijack_setting
                            }
                        }`;

                const groupData = await API.graphql(graphqlOperation(query));
             //   console.log(groupData);
                var groups = groupData.data.getASNGroups;
             //   console.log(groups);

                if (!groups) {
                    this.orgASGroups = [];
                    this.$store.commit("setOrgASGroups", []);
                    //this.loading = false
                    //this.showOrgGroups = true
                } else if (groups.length == 0) {
                    this.orgASGroups = [];
                    this.$store.commit("setOrgASGroups", []);
                    //this.loading = false
                    //this.showOrgGroups = true
                } else {
                    for (var i = 0; i < groups.length; i++) {
                    groups[i].exclude_hij = groups[i].exclude == 1 ? "Exclude" : "Hijack"; //if 1, its hijack score based else it's exclusion based
                    }
                    this.orgASGroups = groups;
                    this.$store.commit("setOrgASGroups", groups);
                    //this.loading = false;
                    //this.showOrgGroups = true
                }
            },
            getUserGroups: async function () {
                //this.loading2 = true;

                var query =
                    `query getASNGroups {
                            getASNGroups (
                                p_organisation: "` +
                    this.organisation +
                    `",
                                p_user: "` +
                    this.username +
                    `"
                            ) {
                                group_name,
                                exclude,
                                hijack_setting
                            }
                        }`;

                const groupData = await API.graphql(graphqlOperation(query));
              //  console.log(groupData);
                var groups = groupData.data.getASNGroups;
              //  console.log(groups);

                if (!groups) {
                    this.userASGroups = [];
                    this.$store.commit("setUserASGroups", []);
                    //this.loading2 = false
                    //this.showUserGroups = true
                } else if (groups.length == 0) {
                    this.userASGroups = [];
                    this.$store.commit("setUserASGroups", []);
                    //this.loading2 = false
                    //this.showUserGroups = true
                } else {
                    for (var i = 0; i < groups.length; i++) {
                    groups[i].exclude_hij = groups[i].exclude == 1 ? "Exclude" : "Hijack"; //if 1, its hijack score based else it's exclusion based
                    }
                    this.userASGroups = groups;
                    this.$store.commit("setUserASGroups", groups);
                    //this.loading2 = false;
                    //this.showUserGroups = true
                }
            },

            processRuleData(rule,scope) {
                let p = rule.filter.prefix.is;
                let o = rule.filter.origin.is;
                let pa = rule.filter.path.contains;
                let np = rule.filter.prefix.isnot;
                let no = rule.filter.origin.isnot;
                let npa = rule.filter.path.notcontains;
                rule.ruletype=scope;
                if (rule.enabled==1) { rule.enabled=true } else { rule.enabled=false }
                rule.filter.prefix.isfull = p;
                rule.filter.origin.isfull = o;
                rule.filter.path.containsfull = pa;
                rule.filter.prefix.isnotfull = np;
                rule.filter.origin.isnotfull = no;
                rule.filter.path.notcontainsfull = npa;
                if (p.length > 2) {
                    p = p.slice(0, 2);
                    let p_more=rule.filter.prefix.is.length-2;
                    p.push("(+"+p_more.toString()+" more)");
                }
                if (o.length > 2) {
                    o = o.slice(0, 2);
                    let o_more=rule.filter.origin.is.length-2;
                    o.push("(+"+o_more.toString()+" more)");
                }
                if (pa.length > 2) {
                    pa = pa.slice(0, 2);
                    let pa_more=rule.filter.path.contains.length-2;
                    pa.push("(+"+pa_more.toString()+" more)");
                }

                if (np.length > 2) {
                    np = np.slice(0, 2);
                    let np_more=rule.filter.prefix.isnot.length-2;
                    np.push("(+"+np_more.toString()+" more)");
                }
                if (no.length > 2) {
                    no = no.slice(0, 2);
                    let no_more=rule.filter.origin.isnot.length-2;
                    no.push("(+"+no_more.toString()+" more)");
                }
                if (npa.length > 2) {
                    npa = npa.slice(0, 2);
                    let npa_more=rule.filter.path.notcontains.length-2;
                    npa.push("(+"+npa_more.toString()+" more)");
                }
                rule.filter.prefix.is = p;
                rule.filter.origin.is = o;
                rule.filter.path.contains = pa;
                rule.filter.prefix.isnot = np;
                rule.filter.origin.isnot = no;
                rule.filter.path.notcontains = npa;
                return rule;
            },
            getRules: async function (scope) {
                this.loading = true;
                this.organisation_download_rules = [];
                var verb=""
                if (scope=="user") {
                    verb="getRulesForUser"
                    this.user_download_rules = [];
                } else {
                    verb="getRulesForOrg"
                    this.organisation_download_rules = [];
                }
                var ruleQuery =
                    `query `+verb+` {
                            `+verb+`(start_time: ` +
                    Math.floor(Date.now() / 1000 - 604800) +
                    `) {
                                id
                                alertgroup
                                alertname
                                overrides {
                                highjackscore
                                impactscore
                                sethijack
                                setimpact
                                }
                                alerts {
                                assertion
                                firstseen
                                pathchange
                                }
                                emails {
                                assertion
                                firstseen
                                pathchange
                                repeat
                                }
                                filter {
                                origin {
                                    direction
                                    filter_asns_in_org
                                    is
                                    isnot
                                }
                                path {
                                    as_in_path
                                    contains
                                    notcontains
                                    pathchange
                                    peer
                                    position
                                }
                                prefix {
                                    is
                                    isin
                                    isnot
                                }
                                scores {
                                    andor
                                    hijack
                                    impact
                                }
                                }
                                notification_count
                                enabled
                            }
                        }
                        `;

                var rules = [];
//                console.log(ruleQuery);
                const ruleData = await API.graphql(graphqlOperation(ruleQuery));
//                console.log(ruleData);
                this.ruleGroups=['Default']
                if (scope=='org'){
                    if (ruleData.data.getRulesForOrg.length == 0 || rules === undefined) {
                    this.orgRules = [];
                    } else {
                    for (var r in Object.entries(ruleData.data.getRulesForOrg)) {
                        let rule = this.processRuleData(ruleData.data.getRulesForOrg[r],"org") ;
                        if (rule.enabled) {
                            rules.push(rule);
                            if (rule.alertgroup.length>0) {
                                if (this.ruleGroups.indexOf(rule.alertgroup)<0) {
                                    this.ruleGroups.push(rule.alertgroup)
                                }
                            }
                        }
                    }
//                    console.log(rules);
                    this.orgRules = rules;
                    }
                }
                if (scope=='user'){
                    if (ruleData.data.getRulesForUser.length == 0 || rules === undefined) {
                    this.userRules = [];
                    } else {
                    for (var r in Object.entries(ruleData.data.getRulesForUser)) {
                        let rule = this.processRuleData(ruleData.data.getRulesForUser[r],"user") ;
                        if (rule.enabled) {
                            rules.push(rule);
                            if (rule.alertgroup.length>0) {
                                if (this.ruleGroups.indexOf(rule.alertgroup)<0) {
                                    this.ruleGroups.push(rule.alertgroup)
                                }
                            }
                        }
                    }
//                    console.log(rules);
                    this.userRules = rules;
                    }
                }
                var i;
                var download_rules=[];
                for (i = 0; i < rules.length; i++) {
                    download_rules.push({
                    'alertname': rules[i].alertname,
                    'alertgroup': rules[i].alertgroup,
                    'alerttype': scope,
                    'assertion': rules[i].alerts.assertion? 'Y' : 'N',
                    'firstseen': rules[i].alerts.firstseen? 'Y' : 'N',
                    'pathchange': rules[i].alerts.pathchange? 'Y' : 'N',
                    'assertion_emails': rules[i].emails.assertion? 'Y' : 'N',
                    'firstseen_emails': rules[i].emails.firstseen? 'Y' : 'N',
                    'pathchange_emails': rules[i].emails.pathchange? 'Y' : 'N',
                    'repeat_emails': rules[i].emails.repeat,
                    'origin_direction': rules[i].filter.origin.direction,
                    'origin_filter_asns_in_org': rules[i].filter.origin.filter_asns_in_org? 'Y' : 'N',
                    'origin_is': rules[i].filter.origin.isfull,
                    'origin_isnot': rules[i].filter.origin.isnotfull,
                    'path_as_in_path': rules[i].filter.path.as_in_path? 'Y' : 'N',
                    'path_as_contains': rules[i].filter.path.containsfull,
                    'path_as_notcontains': rules[i].filter.path.notcontainsfull,
                    'path_as_position': rules[i].filter.path.postition,
                    'prefix_is': rules[i].filter.prefix.isfull,
                    'prefix_isin': rules[i].filter.prefix.isin? 'Y' : 'N',
                    'prefix_isnot': rules[i].filter.prefix.isnotfull,
                    'scores_hijack': rules[i].filter.scores.hijack,
                    'scores_impact': rules[i].filter.scores.impact,
                    'scores_andor': rules[i].filter.scores.andor,
                    'over_seth': rules[i].overrides.sethijack? 'Y' : 'N',
                    'over_hscore': rules[i].overrides.hijackscore,
                    'over_seti': rules[i].overrides.sethimpact? 'Y' : 'N',
                    'over_iscore': rules[i].overrides.impactscore,
                    'enabled': rules[i].enabled
                    })
                }
                if (scope=="user") {
                    this.user_download_rules=download_rules;
                } else {
                    this.organisation_download_rules=download_rules;
                }
                this.loading = false;
            },
        }
    };
</script>
<style scoped>
    
    table.v-table tbody td:first-child, 
    table.v-table tbody td:not(:first-child), 
    table.v-table tbody th:first-child, 
    table.v-table tbody th:not(:first-child), 
    table.v-table thead td:first-child, 
    table.v-table thead td:not(:first-child), 
    table.v-table thead th:first-child, 
    table.v-table thead th:not(:first-child) {
        padding: 0 5px
    }

    table.v-table tbody td, 
    table.v-table tbody th {
        height: 38px;
    }

    .special
    {
        cursor: pointer;
    }


</style>